import { useState } from 'react';
import { OnlineIndicator } from '../../components/OnlineIndicator/OnlineIndicator';

/**
 * Шапка Главной (Экран 2.0) — отличается от обычного `<Header>` кардинально:
 * вместо заголовка экрана здесь логотип «Помощник мебельщика» (Manrope Variable
 * 22px, font-display) и переключатель языка KZ / Рус / UZ.
 *
 * v0.4 (ADR-0023): bg-header (peach #FF8C1A) + height 64px (h-16). Lang switcher
 * fontSize 14px (vs прежние 20px), цвета inactive/separator из ink с opacity
 * (`rgba(0,0,0,0.5)` / `rgba(0,0,0,0.35)`) — v0.4.2 (H2.4) ink → pure black.
 *
 * Block 12 PR-A Phase A5: OnlineIndicator добавлен в правый блок перед language switcher.
 *
 * Phase 1 — переключатель языка визуальный placeholder: кнопки рендерятся,
 * `aria-pressed` обновляется по локальному state, но реального изменения
 * локали не происходит (LinguiJS интегрируется в отдельной фазе после
 * Onboarding). По умолчанию активный — «Рус».
 *
 * Источник истины — `docs/prototypes/_design_spike/screen-home-spike.html`
 * (canonical после ADR-0023). Прежний `screen-2-home.html` deprecated.
 *
 * Почему отдельный компонент, а не `<Header variant='home'>`: layout
 * кардинально другой (логотип + лэнг-свитчер vs заголовок), нет общей JSX
 * базы — `variant`-флаг породил бы 2 disjoint-ветки. Фича-локальное
 * расположение оправдано «3rd consumer trigger» правилом (B-049).
 */

type LangKey = 'kz' | 'ru' | 'uz';

const LANGS: ReadonlyArray<{ key: LangKey; label: string }> = [
  { key: 'kz', label: 'KZ' },
  { key: 'ru', label: 'Рус' },
  { key: 'uz', label: 'UZ' },
];

export function HomeHeader() {
  const [lang, setLang] = useState<LangKey>('ru');

  return (
    <header className="h-16 flex items-center justify-between px-4 bg-header border-b border-header-border">
      <span
        className="font-display text-h-logo text-ink"
        style={{ letterSpacing: '0.02em', lineHeight: 1 }}
      >
        Помощник мебельщика
      </span>
      <div className="flex items-center gap-3">
        <OnlineIndicator />
        <div className="flex items-center gap-0.5" role="group" aria-label="Переключатель языка">
          {LANGS.map(({ key, label }, i) => {
            const isActive = lang === key;
            return (
              <span key={key} className="flex items-center">
                {i > 0 && (
                  <span className="px-0.5" style={{ color: 'rgba(0,0,0,0.35)' }} aria-hidden="true">
                    |
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setLang(key)}
                  aria-label={`Язык ${label}`}
                  aria-pressed={isActive}
                  className="px-1 py-1 focus-visible:outline-none focus-visible:ring-2"
                  style={{
                    color: isActive ? '#000000' : 'rgba(0,0,0,0.5)',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 14,
                  }}
                >
                  {label}
                </button>
              </span>
            );
          })}
        </div>
      </div>
    </header>
  );
}
